import React, { useState, useEffect } from 'react';
import axios from '../../../../utils/axiosConfig';
import { FaCalculator, FaRoute, FaClock, FaMoneyBillWave, FaShieldAlt, FaPercentage } from 'react-icons/fa';
import * as turf from '@turf/turf';
import { getApiBaseUrl } from '../../../../utils/environment';
import { 
  isPointInNairobiCBD, 
  getCBDGeofenceStatus, 
  formatGeofenceStatus 
} from '../../../../utils/nairobiCBDGeofence';
import { calculateCommission } from '../../../../utils/commissionCalculator';
import logger from '../../../../utils/logger';

const PriceCalculator = ({ 
  pickupLocation, 
  deliveryLocation, 
  orderTypeId,
  items = []
}) => {
  const [price, setPrice] = useState(null);
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [distanceMethod, setDistanceMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [breakdown, setBreakdown] = useState({
    basePrice: 0,
    distancePrice: 0,
    itemsPrice: 0
  });
  const [commissionBreakdown, setCommissionBreakdown] = useState({
    companyCommission: 0,
    assistantEarnings: 0,
    companyPercentage: 0,
    assistantPercentage: 0,
    isPickupInCBD: false,
    isDeliveryInCBD: false,
    isBothInCBD: false
  });
  const [showCommissionDetails, setShowCommissionDetails] = useState(false);

  // Calculate price only once when locations are initially set or explicitly triggered
  useEffect(() => {
    // Store the calculation state in a ref to prevent unnecessary recalculations
    const hasValidLocations = 
      pickupLocation && deliveryLocation && 
      pickupLocation.latitude && pickupLocation.longitude &&
      deliveryLocation.latitude && deliveryLocation.longitude;
    
    // Only calculate if we have valid locations and no price has been calculated yet
    if (hasValidLocations && price === null && !loading) {
      calculatePrice();
    }
  }, []);

  // Calculate commission breakdown using the utility function
  const calculateCommissionBreakdown = (totalPrice, pickupLocation, deliveryLocation) => {
    const commission = calculateCommission(totalPrice, pickupLocation, deliveryLocation);
    return commission;
  };

  const calculatePrice = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, calculate distance using OpenStreetMap's OSRM for accurate road distances
      const token = localStorage.getItem('authToken');
      
      // Use the backend's distance calculation endpoint which uses OSRM for accurate road distances
      const distanceResponse = await axios.post(
        `${getApiBaseUrl()}locations/calculate-distance/`,
        {
          start_lat: pickupLocation.latitude,
          start_lng: pickupLocation.longitude,
          end_lat: deliveryLocation.latitude,
          end_lng: deliveryLocation.longitude,
          use_osrm: true // Use OpenStreetMap's OSRM for accurate road distances
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (distanceResponse.data) {
        setDistance(distanceResponse.data.distance);
        setDuration(distanceResponse.data.duration);
        setDistanceMethod(distanceResponse.data.method || 'haversine');

        // Calculate items price
        const itemsPrice = items.reduce((total, item) => {
          return total + (parseFloat(item.price || 0) * parseInt(item.quantity || 1));
        }, 0);

        // Use a dedicated endpoint for price calculation to ensure consistency with backend logic
        try {
          const priceResponse = await axios.post(
            `${getApiBaseUrl()}orders/calculate-price/`,
            {
              distance_km: distanceResponse.data.distance,
              order_type_id: orderTypeId,
              items: items.map(item => ({
                price: parseFloat(item.price || 0),
                quantity: parseInt(item.quantity || 1)
              }))
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (priceResponse.data) {
            // Set price and breakdown from backend response
            const totalPrice = priceResponse.data.total_price;
            setPrice(totalPrice);
            setBreakdown({
              basePrice: priceResponse.data.base_price,
              distancePrice: priceResponse.data.distance_price,
              itemsPrice: priceResponse.data.items_price
            });
            
            // Calculate commission breakdown based on geofencing
            const commissionData = calculateCommissionBreakdown(totalPrice, pickupLocation, deliveryLocation);
            setCommissionBreakdown(commissionData);
          }
        } catch (priceError) {
          logger.log('Price calculation API not available, using frontend calculation');
          
          // Fall back to frontend calculation if the API is not available
          // This ensures the app still works even if the backend endpoint is not implemented yet
          
          // Get order type details for pricing if available
          let basePrice = 250;
          let pricePerKm = 30;

          if (orderTypeId) {
            try {
              const orderTypeResponse = await axios.get(
                `${getApiBaseUrl()}orders/types/${orderTypeId}/`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                }
              );

              if (orderTypeResponse.data) {
                basePrice = parseFloat(orderTypeResponse.data.base_price || 250);
                pricePerKm = parseFloat(orderTypeResponse.data.price_per_km || 30);
              }
            } catch (orderTypeError) {
              logger.log('Order type not found, using default pricing');
            }
          }

          // Calculate distance price using the new pricing model
          const distance = distanceResponse.data.distance;
          const distancePrice = distance <= 5 ? 0 : (distance - 5) * pricePerKm;
          
          // Calculate total price
          const totalPrice = basePrice + distancePrice + itemsPrice;
          
          // Set price and breakdown
          setPrice(totalPrice);
          setBreakdown({
            basePrice,
            distancePrice,
            itemsPrice
          });
          
          // Calculate commission breakdown based on geofencing
          const commissionData = calculateCommissionBreakdown(totalPrice, pickupLocation, deliveryLocation);
          setCommissionBreakdown(commissionData);
        }
      }
    } catch (err) {
      logger.error('Error calculating price:', err);
      
      // Provide more specific error messages based on the error
      if (err.response && err.response.status === 500) {
        setError('Server error. Our team has been notified. Please try again later.');
      } else if (err.message === 'Network Error') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError('Failed to calculate price. Please try again.');
      }
      
      // Fall back to a simple calculation if the API fails
      if (pickupLocation && deliveryLocation) {
        try {
          // Calculate straight-line distance using turf.js
          const point1 = turf.point([pickupLocation.longitude, pickupLocation.latitude]);
          const point2 = turf.point([deliveryLocation.longitude, deliveryLocation.latitude]);
          const distance = turf.distance(point1, point2, { units: 'kilometers' });
          
          setDistance(distance);
          setDistanceMethod('turf');
          
          // Calculate duration (assuming 30 km/h average speed)
          const durationMinutes = (distance / 30) * 60;
          setDuration(durationMinutes);
          
          // Calculate price using the new pricing model
          const basePrice = 250;
          const distancePrice = distance <= 5 ? 0 : (distance - 5) * 30;
          
          // Calculate items price
          const itemsPrice = items.reduce((total, item) => {
            return total + (parseFloat(item.price || 0) * parseInt(item.quantity || 1));
          }, 0);
          
          // Set price and breakdown
          const totalPrice = basePrice + distancePrice + itemsPrice;
          setPrice(totalPrice);
          setBreakdown({
            basePrice,
            distancePrice,
            itemsPrice
          });
          
          // Calculate commission breakdown based on geofencing
          const commissionData = calculateCommissionBreakdown(totalPrice, pickupLocation, deliveryLocation);
          setCommissionBreakdown(commissionData);
          
          // Update error message to indicate we're using a fallback calculation
          setError('Using estimated distance calculation. For more accuracy, please try again later.');
        } catch (fallbackError) {
          logger.error('Fallback calculation failed:', fallbackError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-2"></div>
          <p>Calculating price...</p>
        </div>
      </div>
    );
  }

  // Only show error as a complete replacement if we don't have a price
  if (error && !price) {
    return (
      <div className="bg-red-50 p-4 rounded-lg shadow-sm border border-red-200">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!price && (!pickupLocation || !deliveryLocation)) {
    return (
      <div className="bg-yellow-50 p-4 rounded-lg shadow-sm border border-yellow-200">
        <p className="text-yellow-700">
          <FaCalculator className="inline mr-2" />
          Please select pickup and delivery locations to calculate price.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold flex items-center">
          <FaMoneyBillWave className="text-green-600 mr-2" />
          Price Estimate
        </h3>
        
        {!loading && (
          <button 
            onClick={calculatePrice}
            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 flex items-center"
            disabled={!pickupLocation || !deliveryLocation}
          >
            <FaCalculator className="mr-1" /> {price ? 'Recalculate' : 'Calculate'}
          </button>
        )}
        
        {loading && (
          <div className="text-sm text-gray-500">Calculating...</div>
        )}
      </div>
      
      {/* Show warning if we have an error but still have a price (using fallback) */}
      {error && price && (
        <div className="bg-yellow-50 p-2 rounded-lg border border-yellow-200 mb-3 text-xs text-yellow-700">
          {error}
        </div>
      )}
      
      {distance && (
        <div className="mb-2">
          <div className="flex items-center text-gray-700">
            <FaRoute className="text-blue-500 mr-2" />
            Distance: {distance.toFixed(1)} km
          </div>
          {distanceMethod && (
            <div className="text-xs text-gray-500 ml-6 mt-1">
              Using {distanceMethod === 'osrm' ? 'road distance (OSRM)' : distanceMethod === 'turf' ? 'direct distance (Turf.js)' : 'direct distance (Haversine)'}
            </div>
          )}
        </div>
      )}
      
      {duration && (
        <div className="mb-3 flex items-center text-gray-700">
          <FaClock className="text-purple-500 mr-2" />
          Estimated time: {duration < 60 ? `${Math.round(duration)} minutes` : `${Math.floor(duration/60)} hours ${Math.round(duration % 60)} minutes`}
        </div>
      )}
      
      <div className="border-t border-gray-200 pt-2 mt-2">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Base price (up to 5km):</span>
          <span>KES {breakdown.basePrice.toFixed(2)}</span>
        </div>

        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Distance charge ({distance > 5 ? `${(distance - 5).toFixed(1)}km over 5km` : 'None'}):</span>
          <span>KES {breakdown.distancePrice.toFixed(2)}</span>
        </div>
        
        {breakdown.itemsPrice > 0 && (
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Items total:</span>
            <span>KES {breakdown.itemsPrice.toFixed(2)}</span>
          </div>
        )}
        
        <div className="flex justify-between font-semibold text-lg mt-2 pt-2 border-t border-gray-200">
          <span>Total:</span>
          <span className="text-green-600">KES {price ? price.toFixed(2) : '0.00'}</span>
        </div>
      </div>
      
      {/* Commission Breakdown Section */}
      {price && commissionBreakdown.companyCommission > 0 && (
        <div className="mt-4 border-t border-gray-200 pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center">
              <FaPercentage className="text-blue-500 mr-2" />
              Commission Breakdown
            </h4>
            <button
              onClick={() => setShowCommissionDetails(!showCommissionDetails)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {showCommissionDetails ? 'Hide' : 'Show'} Details
            </button>
          </div>
          
          {/* Geofence Status Indicators */}
          <div className="mb-2 text-xs">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <FaShieldAlt className={`mr-1 ${commissionBreakdown.isPickupInCBD ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={commissionBreakdown.isPickupInCBD ? 'text-green-600' : 'text-gray-500'}>
                  Pickup {commissionBreakdown.isPickupInCBD ? 'in CBD' : 'outside CBD'}
                </span>
              </div>
              <div className="flex items-center">
                <FaShieldAlt className={`mr-1 ${commissionBreakdown.isDeliveryInCBD ? 'text-green-500' : 'text-gray-400'}`} />
                <span className={commissionBreakdown.isDeliveryInCBD ? 'text-green-600' : 'text-gray-500'}>
                  Delivery {commissionBreakdown.isDeliveryInCBD ? 'in CBD' : 'outside CBD'}
                </span>
              </div>
            </div>
            
            {commissionBreakdown.isBothInCBD && (
              <div className="mt-1 text-green-600 font-medium">
                ✓ CBD Service - Enhanced assistant earnings!
              </div>
            )}
          </div>
          
          {showCommissionDetails && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Company Commission ({commissionBreakdown.companyPercentage}%):</span>
                <span className="text-red-600">KES {commissionBreakdown.companyCommission.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Assistant Earnings ({commissionBreakdown.assistantPercentage}%):</span>
                <span className="text-green-600 font-medium">KES {commissionBreakdown.assistantEarnings.toFixed(2)}</span>
              </div>
              
              <div className="bg-blue-50 p-2 rounded text-xs text-blue-700 mt-2">
                <strong>Commission Structure:</strong>
                <ul className="mt-1 space-y-1">
                  <li>• CBD Service (both locations in CBD): Company 30%, Assistant 70%</li>
                  <li>• Standard Service (outside CBD): Company 50%, Assistant 50%</li>
                </ul>
              </div>
            </div>
          )}
          
          {!showCommissionDetails && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Assistant will earn:</span>
              <span className="text-green-600 font-medium">
                KES {commissionBreakdown.assistantEarnings.toFixed(2)} ({commissionBreakdown.assistantPercentage}%)
              </span>
            </div>
          )}
        </div>
      )}
      
      <p className="text-xs text-gray-500 mt-2">
        * Base price of KES 180 covers distances up to 5km. Additional distance is charged at KES 20 per km.
        Distance is calculated using actual road routes when possible for greater accuracy.
        Final price may vary based on actual distance traveled and additional services.
      </p>
    </div>
  );
};

export default PriceCalculator;

